import { useMemo, useContext, useEffect } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { LoyaltyPointsEarningConfiguration } from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import useUserMultiCartHotelItemData from '../hooks/useUserMultiCartHotelItemData'
import offerPageURL from 'lib/offer/offerPageURL'
import { isInstantBookingLEHotelItem, isAccommodationItem, isFlightItem } from 'lib/checkout/checkoutUtils'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { queryKeyAdults, queryKeyCheckIn, queryKeyCheckOut, queryKeyChildren } from 'constants/url'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import MultiCartAccommodationTileComparisonCheckbox from './MultiCartAccommodationTileComparisonCheckbox'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import GeoContext from 'contexts/geoContext'
import MultiCartParticipantLabel from './MultiCartParticipantLabel'
import SharedCartComments from '../Comments/SharedCartComments'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEAdmin } from 'selectors/accountSelectors'
import useCharterRewardAvailability from 'hooks/Flights/useCharterRewardAvailability'
import { computeRewardBundlePrice } from 'lib/maldivesCharter/classicRewardPricing'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'

interface Props {
  multiCartItem: App.MultiCartInstantBookingLEHotelItem
  className?: string
  hideTitleAndDates?: boolean
  hideAvailabilityDetails?: boolean
  disableOverlay?: boolean
  saveForLaterIfUnavailable?: boolean
  tileVariant?: 'default' | 'condensed'
}

function LeHotelMultiCartReviewTile({
  multiCartItem,
  className,
  hideTitleAndDates = false,
  hideAvailabilityDetails = false,
  disableOverlay = false,
  saveForLaterIfUnavailable = true,
  tileVariant = 'default',
}: Props) {
  const intl = useIntl()
  // Extract the checkout item for compatibility with existing hooks
  const item = multiCartItem.item as App.Checkout.InstantBookingLEHotelItem
  const data = useUserMultiCartHotelItemData(item)
  const isInstantBooking = isInstantBookingLEHotelItem(item)

  const multiCartActorRef = MultiCartContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartItemsState = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartItems)
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  const isAdmin = useAppSelector(isLEAdmin)
  const isSharedCart = cartSummary.status === 'loaded' && activeCartId && cartSummary.data[activeCartId]?.type === 'shared'

  useEffect(() => {
    // Only save if explicitly enabled AND item is not bookable AND not already saved/deleted
    if (
      saveForLaterIfUnavailable &&
      !data.isBookable &&
      multiCartItem.state === 'active' && // Only process active items
      !data.loading &&
      activeCartId
    ) {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId,
        itemIds: [multiCartItem.id],
        saveForLater: true,
        skipNotification: true,
      })
    }
  }, [data.isBookable, multiCartItem.state, data.loading, saveForLaterIfUnavailable, activeCartId, multiCartItem.id, multiCartReviewActorRef])

  const hotelItems = useMemo(() => {
    if (cartItemsState.status !== 'loaded' || !activeCartId) {
      return []
    }

    const multiCartItems = Object.values(cartItemsState.data[activeCartId] || {})
    return multiCartItems
      .filter(multiCartItem => multiCartItem.state === 'active') // Only active items (not deleted or save_for_later)
      .map(multiCartItem => multiCartItem.item)
      .filter(isAccommodationItem)
  }, [cartItemsState, activeCartId])

  const bundledExperienceItems = useMemo(() => {
    if (!item.isAccomBundle || cartItemsState.status !== 'loaded' || !activeCartId) {
      return []
    }
    const allItems = Object.values(cartItemsState.data[activeCartId] || {})
    return allItems.filter((cartItem): cartItem is App.MultiCartExperienceItem | App.MultiCartTransferItem => {
      if (cartItem.state !== 'active') return false
      if (cartItem.type !== 'experience' && cartItem.type !== 'transfer') return false
      const child = cartItem.item as App.Checkout.ExperienceItem | App.Checkout.TransferItem
      return child.isAccomBundle === item.isAccomBundle &&
        !!child.bundledItemIds?.includes(item.itemId)
    })
  }, [cartItemsState, activeCartId, item.isAccomBundle, item.itemId])

  const bundledExperienceTotal = useMemo(
    () => bundledExperienceItems.reduce((sum, exp) => sum + (exp.item.total ?? 0), 0),
    [bundledExperienceItems],
  )

  // There's a bug where the package availableRooms can still show a value even when the package is sold out
  const roomsLeft = data.isSoldOut ? 0 : (data.package?.availableRooms ?? 0)
  const image = data.package?.roomType.images[0] || data.offer?.image

  const handleSaveForLater = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId!,
      itemIds: [multiCartItem.id],
      saveForLater: !(multiCartItem.state === 'save_for_later'),
    })
  }

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const bundledItemIds = bundledExperienceItems.map(exp => exp.id)
    const bundledItemStates = Object.fromEntries(bundledExperienceItems.map(exp => [exp.id, exp.state]))

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId!,
      itemIds: [multiCartItem.id, ...bundledItemIds],
      currentStates: {
        [multiCartItem.id]: multiCartItem.state,
        ...bundledItemStates,
      },
    })
  }

  const {
    publicTotal: rawPublicTotal,
    memberTotal: rawMemberTotal,
  } = useMemo(() => getUserCartItemDisplayTotals(data.itemTotals), [data?.itemTotals])

  const publicTotal = item.isAccomBundle ? rawPublicTotal + bundledExperienceTotal : rawPublicTotal
  const memberTotal = item.isAccomBundle && rawMemberTotal > 0 ? rawMemberTotal + bundledExperienceTotal : rawMemberTotal

  // Find the associated reward flight in the cart for reward bundle pricing
  const rewardFlightItem = useMemo(() => {
    if (cartItemsState.status !== 'loaded' || !activeCartId) return undefined
    const allItems = Object.values(cartItemsState.data[activeCartId] || {})
    const flightMultiCartItem = allItems.find(ci =>
      ci.state === 'active' &&
      isFlightItem(ci.item) &&
      ci.item.isClassicReward &&
      ci.item.bundledItemIds?.includes(multiCartItem.item.itemId),
    )
    return flightMultiCartItem?.item as App.Checkout.FlightItem | undefined
  }, [cartItemsState, activeCartId, multiCartItem.item.itemId])

  const rewardPassengers = useMemo(() => {
    if (!rewardFlightItem) return []

    const associatedRoomCount = rewardFlightItem.bundledItemIds?.length ?? 1
    if (associatedRoomCount > 1) {
      return generatePassengersFromOccupants(multiCartItem.occupancy)
    }
    return rewardFlightItem.passengers
  }, [rewardFlightItem, multiCartItem.occupancy])

  const rewardPassengerCount = useMemo(
    () => rewardPassengers.filter(p => p.type !== 'infant').length,
    [rewardPassengers],
  )
  const rewardLegs = useMemo(
    () => rewardFlightItem?.flights.map(f => ({
      journeyKey: f.rewardJourneyKey ?? f.journeyKey,
      legTotal: f.cost ?? 0,
    })) ?? [],
    [rewardFlightItem],
  )
  const {
    available: rewardAvailable,
    pointsPerPerson: rewardPointsPerPerson,
    cashCoPayPerPerson: rewardCashCoPayPerPerson,
  } = useCharterRewardAvailability({
    legs: rewardLegs,
    passengerCount: rewardPassengerCount,
    disabled: !rewardFlightItem || rewardLegs.length === 0,
  })
  const rewardBundlePrice = useMemo(() => {
    if (!rewardFlightItem || !rewardAvailable || rewardPointsPerPerson == null || rewardCashCoPayPerPerson == null) return undefined
    const hotelPrice = data.package?.price ?? 0
    const hotelMemberPrice = data.package?.memberPrice
    return computeRewardBundlePrice({
      hotelPrice,
      hotelMemberPrice: hotelMemberPrice && hotelMemberPrice > 0 ? hotelMemberPrice : undefined,
      rewardPointsTotal: rewardPointsPerPerson * rewardPassengerCount,
      rewardCashCoPayTotal: rewardCashCoPayPerPerson * rewardPassengerCount,
    })
  }, [rewardFlightItem, rewardAvailable, rewardPointsPerPerson, rewardCashCoPayPerPerson, rewardPassengerCount, data.package?.price, data.package?.memberPrice])

  const formattedDateRange = useMemo(() => {
    if (isInstantBooking) {
      const dateRange = formatDynamicDateRange(multiCartItem.checkIn, multiCartItem.checkOut)
      if (!dateRange) return false
      return intl.formatMessage(
        {
          id: 'Zqp-jjEOBwgwe-iluafA',
          defaultMessage: '{dateRange} · {duration, plural, one {# night} other {# nights}}',
          description: 'Date range and stay duration for a LE hotel cart review tile',
        },
        {
          dateRange,
          duration: multiCartItem.duration,
        },
      )
    }
    return false
  }, [intl, isInstantBooking, multiCartItem.checkIn, multiCartItem.checkOut, multiCartItem.duration])

  const formattedOccupancy = useMemo(() => {
    if (!multiCartItem.occupancy) return false
    return formatOccupantsShort(multiCartItem.occupancy)
  }, [multiCartItem.occupancy])

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, data.isUnavailable, !!data.error)

  const offerLink = useMemo(() => {
    const params = new URLSearchParams()

    const itemGroup = hotelItems.filter(isInstantBookingLEHotelItem).filter(i => i.offerId === multiCartItem.offerId && i.checkIn === multiCartItem.checkIn && i.checkOut === multiCartItem.checkOut)
    const rooms = itemGroup.map(i => i.occupancy)

    if (!data.isPast) {
      params.set(queryKeyCheckIn, multiCartItem.checkIn)
      params.set(queryKeyCheckOut, multiCartItem.checkOut)
    }

    params.set(queryKeyAdults, rooms.map(room => room.adults).join(','))
    params.set(queryKeyChildren, rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))

    return offerPageURL(data.offer, params)
  }, [data.offer, hotelItems, multiCartItem, data.isPast])

  const { currentRegionCode } = useContext(GeoContext)

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    if (!data.offer || !data.itemTotals) {
      return {
        calculationRequests: [],
        calculationType: 'exact',
      }
    }
    const calculationRequest = generateLoyaltyPointsCalculatorHotelOptions(data.offer, {
      pkg: data.package,
      price: publicTotal,
      memberPrice: memberTotal,
      currentRegionCode,
      nights: data.package?.duration,
    })

    return {
      calculationRequests: [calculationRequest],
      calculationType: 'exact',
    }
  }, [data.offer, data.package, data.itemTotals, publicTotal, memberTotal, currentRegionCode])

  const priceTotals = useMemo(() => ({
    price: publicTotal,
    memberPrice: memberTotal,
    value: data.itemTotals?.value ?? 0,
  }), [data.itemTotals?.value, memberTotal, publicTotal])

  const filters = useMemo(() => ({
    checkIn: multiCartItem.checkIn,
    checkOut: multiCartItem.checkOut,
    rooms: [multiCartItem.occupancy],
    roomRateId: multiCartItem.roomRateId,
  }), [multiCartItem.checkIn, multiCartItem.checkOut, multiCartItem.occupancy, multiCartItem.roomRateId])

  const isValueBeyondNonMemberPrice = (data.itemTotals?.value ?? 0) > publicTotal
  const shouldDisplayValueUpTo = isValueBeyondNonMemberPrice && (data.package?.shouldDisplayValue === true)

  if (data.loading) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (multiCartItem.state === 'deleted') {
    return <UndoDeletedItemsTile
      title={data.offer?.property.name || data.offer?.name || ''}
      onUndoClick={() => {
        multiCartActorRef.send({
          itemIds: [multiCartItem.id],
          type: 'undo delete',
        })
      }}
    />
  }

  if (tileVariant === 'condensed') {
    const condensedPriceElement = rewardBundlePrice ? (
      <VerticalSpacer gap={4}>
        <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
          <Heading variant="heading6" colour={rewardBundlePrice.memberCash !== undefined ? 'lux-plus' : undefined}>
            <FormattedMessage
              id="V53dchYE9awxDiE72URx"
              defaultMessage="{points} + {cash}"
              description="Reward bundle price on the cart accommodation tile showing loyalty points plus cash co-pay"
              values={{
                points: <FormatLoyaltyPoints points={rewardBundlePrice.points} abbreviated />,
                cash: <FormatCurrency value={rewardBundlePrice.memberCash ?? rewardBundlePrice.cash} format="preciseDollar" />,
              }}
            />
          </Heading>
        </Group>
        {rewardBundlePrice.memberCash !== undefined && (
          <Caption variant="medium" colour="neutral-two">
            <FormattedMessage
              id="iipG8VyXTvvJj7Lza1MZ"
              defaultMessage="Non-member <b>{points} + {cash}</b>"
              description="Non-member reward price caption shown below the LuxPlus member price on the cart review tile"
              values={{
                points: <FormatLoyaltyPoints points={rewardBundlePrice.points} abbreviated />,
                cash: <FormatCurrency value={rewardBundlePrice.cash} format="preciseDollar" />,
                b: (chunks: React.ReactNode) => <b>{chunks}</b>,
              }}
            />
          </Caption>
        )}
      </VerticalSpacer>
    ) : (
      <CartReviewPriceStack
        price={publicTotal}
        memberPrice={memberTotal}
        value={shouldDisplayValueUpTo ? (data.itemTotals?.value ?? 0) : 0}
        luxPlusOfferConfig={data.offer?.luxPlus}
      />
    )

    return (
      <CondensedCartReviewTile
        to={offerLink}
        label={roomsLeft ? <RoomsRemainingLabel roomCount={roomsLeft} threshold="rate" /> : undefined}
        content={<CondensedCartReviewTileContent
          title={data.offer?.property.name || data.offer?.name || ''}
          dates={formattedDateRange}
          packageTitle={data.package?.name}
          occupancy={formattedOccupancy}
          roomName={data.package?.roomType.name}
        />}
        price={condensedPriceElement}
        banner={<CartReviewBanner
          variant={bannerTypeVariant}
        />}
        imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
        onDelete={handleDelete}
      />
    )
  }

  // Only show comments footer for standalone tiles (not in multi-room groups) in shared carts
  const showComments = !hideTitleAndDates && activeCartId && isSharedCart
  const commentsFooter = showComments ? (
    <SharedCartComments
      multiCartId={activeCartId}
      itemIds={[multiCartItem.id]}
      isAdmin={isAdmin}
      noBorder
    />
  ) : undefined

  const fullPriceElement = rewardBundlePrice ? (
    <VerticalSpacer gap={4}>
      <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
        <Heading variant="heading6" colour={rewardBundlePrice.memberCash !== undefined ? 'lux-plus' : undefined}>
          <FormattedMessage
            id="V53dchYE9awxDiE72URx"
            defaultMessage="{points} + {cash}"
            description="Reward bundle price on the cart accommodation tile showing loyalty points plus cash co-pay"
            values={{
              points: <FormatLoyaltyPoints points={rewardBundlePrice.points} abbreviated />,
              cash: <FormatCurrency value={rewardBundlePrice.memberCash ?? rewardBundlePrice.cash} format="preciseDollar" />,
            }}
          />
        </Heading>
      </Group>
      {rewardBundlePrice.memberCash !== undefined && (
        <Caption variant="medium" colour="neutral-two">
          <FormattedMessage
            id="LhV2PlssNEZV4Vyv9DKf"
            defaultMessage="Non-member {points} + {cash}"
            description="Non-member reward price caption shown below the LuxPlus member price on the cart review tile"
            values={{
              points: <b><FormatLoyaltyPoints points={rewardBundlePrice.points} abbreviated /></b>,
              cash: <b><FormatCurrency value={rewardBundlePrice.cash} format="preciseDollar" /></b>,
            }}
          />
        </Caption>
      )}
    </VerticalSpacer>
  ) : (
    <CartReviewPriceStack
      price={publicTotal}
      memberPrice={memberTotal}
      value={shouldDisplayValueUpTo ? (data.itemTotals?.value ?? 0) : 0}
      luxPlusOfferConfig={data.offer?.luxPlus}
    />
  )

  return (
    <CartReviewTile
      label={roomsLeft ? <RoomsRemainingLabel roomCount={roomsLeft} threshold="rate" /> : undefined}
      to={offerLink}
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      content={<CartReviewTileContent
        title={data.offer?.property.name || data.offer?.name || ''}
        dates={formattedDateRange}
        hideTitleAndDates={hideTitleAndDates}
        packageTitle={data.package?.name}
        occupancy={formattedOccupancy}
        loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
        roomName={data.package?.roomType.name}
        cancellationPolicy={data.offer && data.package?.roomRate.cancellationPolicy.type && (
          <TileCancellationPolicy
            cancellationPolicyType={data.package.roomRate.cancellationPolicy.type}
            checkInDate={isInstantBooking ? item.checkIn : ''}
            timezoneOffset={data.offer.property.timezoneOffset || 0}
            offerType={data.offer.type}
            textSize="medium"
          />
        )}
        comparisonCheckbox={<MultiCartAccommodationTileComparisonCheckbox
          offerType={data.offer?.type ?? 'hotel'}
          cartItemId={multiCartItem.id}
          cartId={activeCartId!}
          packageUniqueKey={data.package?.uniqueKey}
          offerId={data.offer?.id || ''}
          name={data.offer?.name || ''}
          filters={filters}
          priceTotals={priceTotals}
        />}
        label={<MultiCartParticipantLabel item={multiCartItem} />}
      />}
      price={fullPriceElement}
      banner={<CartReviewBanner
        variant={bannerTypeVariant}
        to={offerLink}
      />}
      footer={commentsFooter}
      isBookable={data.isBookable}
      saveForLater={multiCartItem.state === 'save_for_later'}
      actions={<CartReviewTileActions
        isBookable={data.isBookable}
        saveForLater={multiCartItem.state === 'save_for_later'}
        onSaveForLater={handleSaveForLater}
        onDelete={handleDelete}
      />}
      className={className}
      hideAvailabilityDetails={hideAvailabilityDetails}
      disableOverlay={disableOverlay}
    />
  )
}

export default LeHotelMultiCartReviewTile
